import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import * as Sentry from '@sentry/react';

import { Advertisable, User } from '@nextroll/ar-base-api';
import { usePageTitle } from '@nextroll/ar-style-base';
import { loadAllClients } from '@nextroll/ar-feature-gating';
import telemetry from '@nextroll/telemetry';

import { getBootstrapData } from 'api/adrollApi';

import { getAppPath, getEnvironment } from '../util/urls';
import { SENTRY_INIT_CONFIG } from '../util/services.sentry';


export interface NavigateToParams {
    to: string;
    replace?: boolean;
    openNewWindow?: boolean;
    target?: string;
}

export interface WorkflowsContextType {
    readonly advertisable?: Advertisable;
    readonly actualUser?: User;
    readonly user?: User;
    readonly navigateTo: { (params: NavigateToParams): void };
    readonly pageTitle?: string;
    readonly setPageTitle: ({ title, subtitle }: {
        title?: string;
        subtitle?: string;
    }) => void;
    readonly isReady: boolean;
}

export const getURLWithAdvertisable = (to?: string, advertisable?: Advertisable): string | undefined => {
    if (!to) {
        return to;
    }

    const [urlWithoutParams, paramsString = ''] = to.split('?');
    const shouldAppendAdvertisable = advertisable && !new URLSearchParams(paramsString).has('advertisable');

    // This is supposed to check that the url is for the current host,
    // like `/settings` but not `//google.com`.
    if (advertisable && to.length > 2 && to[0] === '/' && to[1] !== '/' && shouldAppendAdvertisable) {
        const urlSearchParams = new URLSearchParams(paramsString);
        urlSearchParams.set('advertisable', advertisable.eid);
        return `${urlWithoutParams}?${urlSearchParams}`;
    }
    return to;
};

const useContainerContext = () => {
    const history = useHistory();

    const [advertisable, setAdvertisable] = useState<Advertisable | undefined>(undefined);
    const [actualUser, setActualUser] = useState<User | undefined>(undefined);
    const [user, setUser] = useState<User | undefined>(undefined);
    const [pageTitle, setPageTitle] = usePageTitle({ advertisable, disableRollWorksTheme: true }) as [string, WorkflowsContextType['setPageTitle']];
    const [isReady, setIsReady] = useState(false);

    /**
     * utility method to navigate within the app
     */
    const navigateTo = useCallback(async (navigateToParams: NavigateToParams) => {
        const {
            to,
            openNewWindow = false,
            target = '_blank', // only used if openNewWindow is set to true
            replace = false,
        } = { ...navigateToParams };

        const url = getURLWithAdvertisable(to, advertisable);
        if (!url) {
            return; // no valid URL; do nothing
        }

        if (openNewWindow) {
            window.open(url, target, 'noopener,noreferrer');
            return;
        }

        if (/^(\/workflows\/|\/workflows\?)/i.test(to)) {
            history[replace ? 'replace' : 'push'](url);
        } else {
            window.location.href = url;
        }
    }, [history, advertisable]);

    /**
     * load product bootstrap data
     */
    const load = useRef(async () => {
        const environment = getEnvironment();

        // initialize tracking libraries
        Sentry.init(SENTRY_INIT_CONFIG);
        telemetry.load({
            appVersion: `rofl-ui-${process.env.VERSION}`,
            env: environment,
            heap: true,
            segment: true,
            userflow: true,
        });

        try {
            // bootstrap data, including user and advertisable info
            const data = await getBootstrapData(new URLSearchParams(window?.location.search).get('advertisable') || '');
            setActualUser(data.actualUser);
            setAdvertisable(data.advertisable);
            setUser(data.user);

            // feature and provisioning client
            await loadAllClients({
                splitOpts: { ...data, environment },
                provisioningOpts: { advertisable: data.advertisable, environment },
                userPrefsOpts: { ...data, environment },
            });
        } catch (error) {
            console.error('Failed to load bootstrap data', error);
            Sentry.captureException(error);

            // redirect to the not-found page with a real URL, not the route pattern
            // from getWorkflowNotFoundRoute(), otherwise the literal ":pathId" ends
            // up in the browser URL
            history.replace(getAppPath('/notfound'));
        } finally {
            setIsReady(true);
        }
    });
    useEffect(() => {
        !isReady && load.current();
    }, [isReady]);

    return {
        advertisable,
        actualUser,
        user,
        navigateTo,
        pageTitle,
        setPageTitle,
        isReady,
    };
};

export default useContainerContext;
